import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faEnvelope, faPhoneAlt, faUnlockAlt, faUser } from "@fortawesome/free-solid-svg-icons";
import { Button, Card, Col, Container, Form, InputGroup, Row } from "@themesberg/react-bootstrap";
import { Link, useHistory, useLocation } from "react-router-dom";

import { Routes } from "../routes";
import BgImage from "../assets/img/illustrations/signin.svg";
import { registerVendor, verifyVendorInvite } from "../api/vendorRegistration.api";
import { useToast } from "../components/ToastProvider";

const getTokenFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return params.get("token") || "";
};

export default () => {
  const history = useHistory();
  const location = useLocation();
  const { showToast } = useToast();
  const [token] = useState(() => getTokenFromSearch(location.search));
  const [inviteEmail, setInviteEmail] = useState("");
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    contact: "",
    password: "",
    confirmPassword: ""
  });

  useEffect(() => {
    let isMounted = true;

    const loadInvite = async () => {
      if (!token) {
        showToast({ message: "Invitation token is missing.", variant: "danger" });
        setLoadingInvite(false);
        return;
      }

      try {
        const response = await verifyVendorInvite(token);

        if (!isMounted) {
          return;
        }

        setInviteEmail(response.email || "");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        showToast({ message: error.message || "Invalid or expired invitation.", variant: "danger" });
      } finally {
        if (isMounted) {
          setLoadingInvite(false);
        }
      }
    };

    loadInvite();

    return () => {
      isMounted = false;
    };
  }, [token, showToast]);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((currentFormData) => ({
      ...currentFormData,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      showToast({ message: "Passwords do not match.", variant: "warning" });
      return;
    }

    setSubmitting(true);

    try {
      const response = await registerVendor({
        token,
        first_name: formData.first_name,
        last_name: formData.last_name,
        contact: formData.contact,
        password: formData.password
      });

      showToast({ message: response.message || "Vendor account created successfully.", variant: "success" });
      setTimeout(() => {
        history.push(Routes.Signin.path);
      }, 1200);
    } catch (error) {
      showToast({ message: error.message || "Registration failed.", variant: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <p className="text-center">
            <Card.Link as={Link} to={Routes.Signin.path} className="text-gray-700">
              <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> Back to sign in
            </Card.Link>
          </p>
          <Row className="justify-content-center form-bg-image" style={{ backgroundImage: `url(${BgImage})` }}>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="mb-4 mb-lg-0 bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h3 className="mb-0">Complete Vendor Registration</h3>
                  <p className="text-gray mb-0 mt-2">Use the invited email and finish your vendor account setup.</p>
                </div>

                {loadingInvite ? (
                  <div className="text-center py-5 text-muted">
                    Validating invite...
                  </div>
                ) : (
                  <Form className="mt-4" onSubmit={handleSubmit}>
                    <Form.Group id="email" className="mb-4">
                      <Form.Label>Email</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control type="email" value={inviteEmail} disabled readOnly />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group id="first_name" className="mb-4">
                      <Form.Label>First Name</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUser} />
                        </InputGroup.Text>
                        <Form.Control
                          autoFocus
                          required
                          type="text"
                          name="first_name"
                          placeholder="First name"
                          value={formData.first_name}
                          onChange={handleChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group id="last_name" className="mb-4">
                      <Form.Label>Last Name</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUser} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="text"
                          name="last_name"
                          placeholder="Last name"
                          value={formData.last_name}
                          onChange={handleChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group id="contact" className="mb-4">
                      <Form.Label>Contact Number</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faPhoneAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="text"
                          name="contact"
                          placeholder="Contact number"
                          value={formData.contact}
                          onChange={handleChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group id="password" className="mb-4">
                      <Form.Label>Password</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="password"
                          name="password"
                          placeholder="Password"
                          value={formData.password}
                          onChange={handleChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Form.Group id="confirmPassword" className="mb-4">
                      <Form.Label>Confirm Password</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faUnlockAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                        />
                      </InputGroup>
                    </Form.Group>

                    <Button variant="primary" type="submit" className="w-100" disabled={submitting || !inviteEmail}>
                      {submitting ? "Creating account..." : "Complete Registration"}
                    </Button>
                  </Form>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};
